/**
 * Shared helper: given an image File and an optional canvas position, create
 * an optimistic image frame with a blob-URL thumbnail, upload the file, then
 * patch the frame with the permanent URL and flip its status to idle.
 *
 * The file's pixel size is read locally first, so the frame is created at
 * the image's aspect, and both image entries carry `width/height`.
 *
 * Used by the workspace paste and drop handlers (canvas and wall) and the
 * Doc editor's inline paste handler. Returns the new frame's ID so callers
 * can link inline thumbnails back to the frame (for click-to-pan).
 */

import { createFrame, updateFrame } from "@/api/spaces";
import { uploadImage } from "@/api/uploads";
import {
  imageFrameSize,
  imagePixelSize,
  readImageFileSize,
} from "@/lib/image-frame-size";
import { fileKind } from "@/lib/file-kinds";
import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

export class UnsupportedImageError extends Error {
  constructor(mimeType: string) {
    super(`Unsupported image format: ${mimeType}`);
    this.name = "UnsupportedImageError";
  }
}

export interface PasteToImageFrameCtx {
  projectId: string;
  spaceId: string;
  /** Canvas position; without one the backend places the frame (the wall). */
  x?: number;
  y?: number;
  /** Optional upstream frame (e.g. the Doc the image was pasted into). */
  sourceFrameId?: string;
}

export interface PasteToImageFrameResult {
  frameId: string;
  url: string;
}

export async function pasteToImageFrame(
  file: File,
  ctx: PasteToImageFrameCtx,
): Promise<PasteToImageFrameResult> {
  if (fileKind(file) !== "image") {
    throw new UnsupportedImageError(file.type || "unknown");
  }

  const localSize = await readImageFileSize(file);
  const { width, height } = imageFrameSize(localSize ?? {});
  const blobUrl = URL.createObjectURL(file);
  const source = ctx.sourceFrameId
    ? { source_frame_id: ctx.sourceFrameId }
    : {};

  // 1. Optimistic frame with blob URL
  const frame = await createFrame(ctx.projectId, ctx.spaceId, {
    frame_type: "image",
    title: translate(libChatMessages, "pastedImage"),
    content: {
      images: [{ url: blobUrl, alt: file.name, ...localSize }],
      ...source,
    },
    ...(ctx.x !== undefined && ctx.y !== undefined
      ? { position_x: ctx.x, position_y: ctx.y }
      : {}),
    width,
    height,
  });

  // 2. Upload + swap URL
  try {
    const uploaded = await uploadImage(file);
    await updateFrame(ctx.projectId, ctx.spaceId, frame.id, {
      content: {
        images: [
          {
            url: uploaded.url,
            alt: uploaded.original_filename,
            ...(imagePixelSize(uploaded) ?? localSize),
          },
        ],
        ...source,
      },
      status: "idle",
    });
    return { frameId: frame.id, url: uploaded.url };
  } catch (err) {
    // Leave the blob URL in place so the user still sees something, but flag error.
    await updateFrame(ctx.projectId, ctx.spaceId, frame.id, {
      status: "error",
    }).catch(() => {});
    throw err;
  } finally {
    // Revoke after the PATCH has resolved (image already re-rendered with new URL).
    // A small timeout keeps the blob valid through any in-flight renders.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
}
